/**
 * Sección Inteligencia & Alt-Data.
 * Consume /api/v1/alt-data/* (SEC EDGAR, FRED, CoinGecko, Yahoo, OpenSky, GDELT, FIRMS, HN, WB, USPTO).
 */
import { h, apiClient, btn, pill, emptyState, formatEur } from './_common.js';

export async function renderInteligencia(root) {
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' }, [
    h('div', { class: 'flex items-center justify-between mb-6 flex-wrap gap-3' }, [
      h('div', {}, [
        h('h2', { class: 'font-display text-3xl font-bold' }, '🛰️ Inteligencia & Alt-Data'),
        h('p', { class: 'text-ink-300' }, 'Fuentes públicas profesionales: SEC, FRED, CoinGecko, Yahoo, OpenSky, GDELT, NASA, USPTO.'),
      ]),
    ]),
    h('div', { class: 'flex gap-1 mb-4 border-b border-base-700 overflow-x-auto' }, [
      tab('fuentes', '📚 Fuentes', true),
      tab('cripto', '₿ Crypto'),
      tab('sec', '🇺🇸 SEC'),
    ]),
    h('div', { id: 'int-content' }),
  ]);
  root.appendChild(wrap);

  wrap.querySelectorAll('[data-int-tab]').forEach((b) => {
    b.addEventListener('click', () => {
      wrap.querySelectorAll('[data-int-tab]').forEach((x) => x.className = tabClass(false));
      b.className = tabClass(true);
      renderTab(b.dataset.intTab, wrap);
    });
  });
  renderTab('fuentes', wrap);
}

function tab(id, label, active = false) { return h('button', { 'data-int-tab': id, class: tabClass(active) }, label); }
function tabClass(a) {
  return a
    ? 'px-3 py-2 text-sm font-medium border-b-2 border-gold-400 text-gold-300 whitespace-nowrap'
    : 'px-3 py-2 text-sm font-medium border-b-2 border-transparent text-ink-300 hover:text-ink-100 whitespace-nowrap';
}

async function renderTab(id, wrap) {
  const content = wrap.querySelector('#int-content');
  content.innerHTML = '<p class="text-ink-300 text-sm">Cargando…</p>';
  if (id === 'fuentes') return renderSources(content);
  if (id === 'cripto') return renderCrypto(content);
  if (id === 'sec') return renderSec(content);
}

// ============================================================
// FUENTES
// ============================================================
async function renderSources(content) {
  const r = await apiClient.get('/api/v1/alt-data/sources').catch(() => ({ sources: [], envKeysPresent: {} }));
  content.innerHTML = '';
  content.appendChild(h('p', { class: 'text-sm text-ink-300 mb-4' },
    '11 fuentes profesionales. Las que requieren API key (FRED, Etherscan, FIRMS) muestran instrucciones de setup.',
  ));
  const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
  r.sources.forEach((s) => grid.appendChild(
    h('div', { class: `bg-base-800/50 border ${s.ready ? 'border-base-700' : 'border-base-600'} rounded-xl p-4` }, [
      h('div', { class: 'flex items-start gap-3' }, [
        h('div', { class: 'text-3xl' }, s.emoji),
        h('div', { class: 'flex-1' }, [
          h('div', { class: 'flex items-center gap-2 flex-wrap' }, [
            h('span', { class: 'font-semibold' }, s.name),
            s.ready ? pill('Activa', 'green') : pill(s.auth === 'free-key' ? 'Falta API key' : 'No disponible', 'gold'),
          ]),
          h('p', { class: 'text-xs text-ink-300 mt-1' }, s.description),
          !s.ready && s.auth === 'free-key' ? h('p', { class: 'text-xs text-gold-300 mt-1' }, `Añade ${envKeyName(s.id)} en /etc/carlos-os/.env`) : null,
        ]),
      ]),
    ]),
  ));
  content.appendChild(grid);
}
function envKeyName(id) {
  if (id === 'fred') return 'FRED_API_KEY';
  if (id === 'etherscan') return 'ETHERSCAN_API_KEY';
  if (id === 'firms') return 'FIRMS_API_KEY';
  return '';
}

// ============================================================
// MERCADOS (Yahoo Finance)
// ============================================================
async function renderMarkets(content) {
  content.innerHTML = '';
  const symbolsInput = h('input', { type: 'text', value: 'AAPL,MSFT,GOOGL,NVDA,TSLA,SPY,QQQ', class: 'flex-1 bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' });
  const out = h('div');
  content.appendChild(h('div', { class: 'flex gap-2 mb-4 flex-wrap items-end' }, [
    h('div', { class: 'flex-1 min-w-[200px]' }, [h('label', { class: 'block text-xs text-ink-300 mb-1' }, 'Símbolos (coma-separados)'), symbolsInput]),
    btn('Cotizar', () => loadQuotes(symbolsInput.value, out)),
  ]));
  content.appendChild(out);
  loadQuotes(symbolsInput.value, out);
}
async function loadQuotes(symbols, container) {
  container.innerHTML = '<p class="text-ink-300 text-sm">Consultando Yahoo Finance…</p>';
  try {
    const r = await apiClient.get(`/api/v1/alt-data/yahoo/quote?symbols=${encodeURIComponent(symbols)}`);
    container.innerHTML = '';
    const table = h('table', { class: 'w-full text-sm' }, [
      h('thead', {}, h('tr', { class: 'text-left text-ink-300 border-b border-base-700' }, [
        h('th', { class: 'py-2' }, 'Símbolo'),
        h('th', {}, 'Nombre'),
        h('th', {}, 'Precio'),
        h('th', {}, 'Var %'),
        h('th', {}, 'Mkt Cap'),
        h('th', {}, 'P/E'),
        h('th', {}, '52w'),
      ])),
      h('tbody', {}, r.quotes.map((q) => h('tr', { class: 'border-b border-base-700/40' }, [
        h('td', { class: 'py-2 font-mono' }, q.symbol),
        h('td', { class: 'max-w-xs truncate' }, q.shortName || q.longName || ''),
        h('td', { class: 'text-gold-300' }, `${q.regularMarketPrice?.toFixed(2)} ${q.currency}`),
        h('td', { class: q.regularMarketChangePercent >= 0 ? 'text-emerald-300' : 'text-red-300' }, `${q.regularMarketChangePercent?.toFixed(2)}%`),
        h('td', {}, q.marketCap ? `${(q.marketCap / 1e9).toFixed(1)}B` : '—'),
        h('td', {}, q.trailingPE ? q.trailingPE.toFixed(1) : '—'),
        h('td', { class: 'text-xs text-ink-300' }, q.fiftyTwoWeekLow ? `${q.fiftyTwoWeekLow.toFixed(0)}-${q.fiftyTwoWeekHigh?.toFixed(0)}` : '—'),
      ]))),
    ]);
    container.appendChild(table);
  } catch (e) {
    container.innerHTML = '';
    container.appendChild(h('p', { class: 'text-red-400 text-sm' }, `Error: ${e.message}`));
  }
}

// ============================================================
// CRIPTO (CoinGecko)
// ============================================================
async function renderCrypto(content) {
  content.innerHTML = '<p class="text-ink-300 text-sm">Consultando CoinGecko…</p>';
  try {
    const [topR, globalR] = await Promise.all([
      apiClient.get('/api/v1/alt-data/coingecko/top?limit=30'),
      apiClient.get('/api/v1/alt-data/coingecko/global'),
    ]);
    content.innerHTML = '';
    if (globalR?.data) {
      const g = globalR.data;
      content.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-4' }, [
        statBox('Market Cap', `$${(g.total_market_cap.usd / 1e12).toFixed(2)}T`),
        statBox('Vol 24h', `$${(g.total_volume.usd / 1e9).toFixed(1)}B`),
        statBox('BTC.D', `${g.market_cap_percentage.btc?.toFixed(1)}%`),
        statBox('ETH.D', `${g.market_cap_percentage.eth?.toFixed(1)}%`),
      ]));
    }
    const grid = h('div', { class: 'overflow-auto' }, h('table', { class: 'w-full text-sm' }, [
      h('thead', {}, h('tr', { class: 'text-left text-ink-300 border-b border-base-700' }, [
        h('th', { class: 'py-2' }, '#'),
        h('th', {}, 'Coin'),
        h('th', {}, 'Precio'),
        h('th', {}, '24h'),
        h('th', {}, '7d'),
        h('th', {}, '30d'),
        h('th', {}, 'Mkt Cap'),
      ])),
      h('tbody', {}, topR.coins.map((c) => h('tr', { class: 'border-b border-base-700/40' }, [
        h('td', { class: 'py-2 text-ink-400' }, String(c.market_cap_rank)),
        h('td', { class: 'font-mono uppercase' }, `${c.symbol}`),
        h('td', { class: 'text-gold-300' }, `$${c.current_price?.toLocaleString('en-US', { maximumFractionDigits: c.current_price < 1 ? 6 : 2 })}`),
        h('td', { class: pctClass(c.price_change_percentage_24h) }, `${c.price_change_percentage_24h?.toFixed(2)}%`),
        h('td', { class: pctClass(c.price_change_percentage_7d_in_currency) }, `${c.price_change_percentage_7d_in_currency?.toFixed(2)}%`),
        h('td', { class: pctClass(c.price_change_percentage_30d_in_currency) }, `${c.price_change_percentage_30d_in_currency?.toFixed(2)}%`),
        h('td', {}, `$${(c.market_cap / 1e9).toFixed(1)}B`),
      ]))),
    ]));
    content.appendChild(grid);
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(h('p', { class: 'text-red-400 text-sm' }, `Error: ${e.message}`));
  }
}
function pctClass(v) { return v == null ? '' : v >= 0 ? 'text-emerald-300' : 'text-red-300'; }

// ============================================================
// MACRO (FRED + World Bank)
// ============================================================
async function renderMacro(content) {
  content.innerHTML = '';
  const series = [
    { id: 'GDPC1', label: 'GDP Real US' },
    { id: 'UNRATE', label: 'Desempleo US' },
    { id: 'CPIAUCSL', label: 'CPI US' },
    { id: 'DFF', label: 'Fed Funds Rate' },
    { id: 'DGS10', label: 'Treasury 10Y' },
    { id: 'DGS2', label: 'Treasury 2Y' },
    { id: 'DEXUSEU', label: 'USD/EUR' },
    { id: 'GVZCLS', label: 'Vol del Oro' },
  ];
  const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3' });
  series.forEach((s) => {
    const card = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
      h('h3', { class: 'font-semibold' }, s.label),
      h('p', { class: 'text-xs text-ink-400 font-mono' }, s.id),
      h('div', { id: `fred-${s.id}`, class: 'text-2xl font-display font-bold mt-2 text-gold-300' }, '…'),
    ]);
    grid.appendChild(card);
  });
  content.appendChild(h('p', { class: 'text-sm text-ink-300 mb-3' }, 'Macro USA (FRED). Requiere FRED_API_KEY en .env del VPS.'));
  content.appendChild(grid);
  series.forEach(async (s) => {
    try {
      const r = await apiClient.get(`/api/v1/alt-data/fred?series=${s.id}&limit=2`);
      const last = r.data?.[0];
      const target = document.getElementById(`fred-${s.id}`);
      if (target) target.textContent = last?.value != null ? `${last.value} (${last.date})` : '—';
    } catch (e) {
      const target = document.getElementById(`fred-${s.id}`);
      if (target) target.textContent = e.message.includes('FRED_API_KEY') ? '🔑 Sin API key' : '—';
    }
  });
}

// ============================================================
// SEC EDGAR — delegado a módulo compartido _sec.js
// ============================================================
async function renderSec(content) {
  await renderSecExplorer(content, 'AAPL');
}

// ============================================================
// GDELT
// ============================================================
async function renderGdelt(content) {
  content.innerHTML = '';
  const qInput = h('input', { type: 'text', value: 'tesla layoffs', class: 'flex-1 bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' });
  const out = h('div');
  content.appendChild(h('div', { class: 'flex gap-2 mb-4 items-end' }, [
    h('div', { class: 'flex-1' }, [h('label', { class: 'block text-xs text-ink-300 mb-1' }, 'Query GDELT'), qInput]),
    btn('Buscar', async () => {
      out.innerHTML = '<p class="text-ink-300 text-sm">Consultando GDELT…</p>';
      try {
        const r = await apiClient.get(`/api/v1/alt-data/gdelt?query=${encodeURIComponent(qInput.value)}&max=30`);
        out.innerHTML = '';
        const articles = r?.articles || [];
        if (articles.length === 0) {
          out.appendChild(emptyState('🌐', 'Sin resultados', 'Prueba otra query GDELT.'));
          return;
        }
        articles.forEach((a) => out.appendChild(
          h('div', { class: 'bg-base-800/50 border border-base-700 rounded-lg p-3 mb-2' }, [
            h('a', { href: a.url, target: '_blank', rel: 'noopener', class: 'block font-semibold hover:text-gold-300' }, a.title || a.url),
            h('div', { class: 'text-xs text-ink-400 mt-1' }, `${a.domain} · ${a.seendate || ''} · ${a.sourcecountry || ''}`),
          ]),
        ));
      } catch (err) {
        out.innerHTML = '';
        out.appendChild(h('p', { class: 'text-red-400 text-sm' }, `Error: ${err.message}`));
      }
    }),
  ]));
  content.appendChild(out);
}

// ============================================================
// SATÉLITE (FIRMS)
// ============================================================
async function renderSatellite(content) {
  content.innerHTML = '';
  content.appendChild(h('p', { class: 'text-sm text-ink-300 mb-3' }, 'Focos de calor (incendios, plantas industriales activas, gas flares) detectados por satélites VIIRS/MODIS. Requiere FIRMS_API_KEY.'));
  const areaInput = h('input', { type: 'text', value: 'ESP', maxlength: 3, class: 'w-32 bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm uppercase' });
  const out = h('div');
  content.appendChild(h('div', { class: 'flex gap-2 mb-4 items-end' }, [
    h('div', {}, [h('label', { class: 'block text-xs text-ink-300 mb-1' }, 'País (ISO3)'), areaInput]),
    btn('Cargar focos', async () => {
      out.innerHTML = '<p class="text-ink-300 text-sm">Consultando NASA FIRMS…</p>';
      try {
        const r = await apiClient.get(`/api/v1/alt-data/firms?area=${areaInput.value.toUpperCase()}&days=1`);
        out.innerHTML = '';
        out.appendChild(h('p', { class: 'font-semibold mb-2' }, `${r.lines} focos detectados (último día)`));
        const preview = (r.csv || '').split('\n').slice(0, 11).join('\n');
        out.appendChild(h('pre', { class: 'text-xs bg-base-900 border border-base-700 rounded-lg p-3 overflow-auto' }, preview));
      } catch (err) {
        out.innerHTML = '';
        out.appendChild(h('p', { class: 'text-red-400 text-sm' }, `Error: ${err.message}`));
      }
    }),
  ]));
  content.appendChild(out);
}

// ============================================================
// VUELOS (OpenSky)
// ============================================================
async function renderFlights(content) {
  content.innerHTML = '';
  content.appendChild(h('p', { class: 'text-sm text-ink-300 mb-3' }, 'Tráfico aéreo en bbox (ADS-B). Por defecto Alicante.'));
  const bboxes = {
    Alicante: { lamin: 38.2, lomin: -0.7, lamax: 38.5, lomax: -0.2 },
    Madrid: { lamin: 40.3, lomin: -3.9, lamax: 40.6, lomax: -3.5 },
    Suez: { lamin: 29.5, lomin: 32.0, lamax: 31.5, lomax: 33.5 },
    PanamaCanal: { lamin: 8.9, lomin: -80.2, lamax: 9.5, lomax: -79.3 },
  };
  const sel = h('select', { class: 'bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' },
    Object.keys(bboxes).map((k) => h('option', { value: k }, k)));
  const out = h('div');
  content.appendChild(h('div', { class: 'flex gap-2 mb-4 items-end' }, [
    h('div', {}, [h('label', { class: 'block text-xs text-ink-300 mb-1' }, 'Zona'), sel]),
    btn('Cargar vuelos', async () => {
      const b = bboxes[sel.value];
      out.innerHTML = '<p class="text-ink-300 text-sm">Consultando OpenSky…</p>';
      try {
        const r = await apiClient.get(`/api/v1/alt-data/opensky/bbox?lamin=${b.lamin}&lomin=${b.lomin}&lamax=${b.lamax}&lomax=${b.lomax}`);
        out.innerHTML = '';
        out.appendChild(h('p', { class: 'font-semibold mb-2' }, `${r.total} aeronaves activas`));
        const tbl = h('table', { class: 'w-full text-sm' }, [
          h('thead', {}, h('tr', { class: 'text-left text-ink-300 border-b border-base-700' }, [
            h('th', { class: 'py-2' }, 'Callsign'),
            h('th', {}, 'País'),
            h('th', {}, 'Alt (m)'),
            h('th', {}, 'Vel (m/s)'),
            h('th', {}, 'On ground'),
          ])),
          h('tbody', {}, r.states.slice(0, 30).map((s) => h('tr', { class: 'border-b border-base-700/40' }, [
            h('td', { class: 'py-2 font-mono' }, s.callsign || s.icao24),
            h('td', {}, s.origin_country),
            h('td', {}, s.baro_altitude != null ? Math.round(s.baro_altitude).toString() : '—'),
            h('td', {}, s.velocity != null ? s.velocity.toFixed(1) : '—'),
            h('td', {}, s.on_ground ? '🛬' : '✈️'),
          ]))),
        ]);
        out.appendChild(tbl);
      } catch (err) {
        out.innerHTML = '';
        out.appendChild(h('p', { class: 'text-red-400 text-sm' }, `Error: ${err.message}`));
      }
    }),
  ]));
  content.appendChild(out);
}

// ============================================================
// PATENTES (USPTO)
// ============================================================
async function renderPatents(content) {
  content.innerHTML = '';
  const qInput = h('input', { type: 'text', value: 'artificial intelligence inference', class: 'flex-1 bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-sm' });
  const out = h('div');
  content.appendChild(h('div', { class: 'flex gap-2 mb-4 items-end' }, [
    h('div', { class: 'flex-1' }, [h('label', { class: 'block text-xs text-ink-300 mb-1' }, 'Búsqueda USPTO'), qInput]),
    btn('Buscar', async () => {
      out.innerHTML = '<p class="text-ink-300 text-sm">Consultando USPTO…</p>';
      try {
        const r = await apiClient.get(`/api/v1/alt-data/uspto?query=${encodeURIComponent(qInput.value)}&rows=20`);
        out.innerHTML = '';
        const patents = r?.patents || [];
        if (patents.length === 0) {
          out.appendChild(emptyState('📜', 'Sin resultados', 'Prueba otros términos.'));
          return;
        }
        const tbl = h('table', { class: 'w-full text-sm' }, [
          h('thead', {}, h('tr', { class: 'text-left text-ink-300 border-b border-base-700' }, [
            h('th', { class: 'py-2' }, '#'),
            h('th', {}, 'Título'),
            h('th', {}, 'Asignee'),
            h('th', {}, 'Fecha'),
          ])),
          h('tbody', {}, patents.map((p) => h('tr', { class: 'border-b border-base-700/40' }, [
            h('td', { class: 'py-2 font-mono' }, p.patent_number),
            h('td', { class: 'max-w-md' }, p.patent_title),
            h('td', {}, (p.assignees?.[0]?.assignee_organization) || '—'),
            h('td', {}, p.patent_date || ''),
          ]))),
        ]);
        out.appendChild(tbl);
      } catch (err) {
        out.innerHTML = '';
        out.appendChild(h('p', { class: 'text-red-400 text-sm' }, `Error: ${err.message}`));
      }
    }),
  ]));
  content.appendChild(out);
}

// ============================================================
// Hacker News
// ============================================================
async function renderHn(content) {
  content.innerHTML = '<p class="text-ink-300 text-sm">Cargando Hacker News…</p>';
  try {
    const r = await apiClient.get('/api/v1/alt-data/hn/top?limit=25');
    content.innerHTML = '';
    r.stories.forEach((s) => content.appendChild(
      h('div', { class: 'bg-base-800/50 border border-base-700 rounded-lg p-3 mb-2 flex items-center gap-3' }, [
        h('span', { class: 'text-gold-300 font-bold w-12 text-right' }, String(s.score)),
        h('div', { class: 'flex-1' }, [
          s.url
            ? h('a', { href: s.url, target: '_blank', rel: 'noopener', class: 'font-semibold hover:text-gold-300' }, s.title)
            : h('span', { class: 'font-semibold' }, s.title),
          h('div', { class: 'text-xs text-ink-400 mt-1' }, `${s.by} · ${s.descendants || 0} comments`),
        ]),
      ]),
    ));
  } catch (err) {
    content.innerHTML = '';
    content.appendChild(h('p', { class: 'text-red-400 text-sm' }, `Error: ${err.message}`));
  }
}

// ============================================================
// Helpers
// ============================================================
function statBox(label, value) {
  return h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl p-4' }, [
    h('div', { class: 'text-xs text-ink-300' }, label),
    h('div', { class: 'text-2xl font-display font-bold mt-1 text-gold-300' }, String(value ?? '—')),
  ]);
}

// Eliminar warning de no-uso
void formatEur;
